/**
 * PATH: apps/quote/src/components/builder/panels/ai-assistant/AuditResultDisplay.tsx
 * PURPOSE: Audit result display with issue cards
 */

import { useState } from 'react'
import { ShieldCheck, XCircle, AlertTriangle, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { QuoteAuditResult } from '@/lib/quoteApi'

function AuditIssueCard({
  issue,
  type,
}: {
  issue: { description: string; severity?: string; action?: string }
  type: 'issue' | 'warning' | 'suggestion'
}) {
  const icons = {
    issue: <XCircle className="w-4 h-4 text-red-500" />,
    warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    suggestion: <Lightbulb className="w-4 h-4 text-blue-500" />,
  }
  const bgColors = {
    issue: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    suggestion: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
  }

  return (
    <div className={cn('p-2 rounded-lg border', bgColors[type])}>
      <div className="flex items-start gap-2">
        {icons[type]}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-900 dark:text-white">{issue.description}</p>
          {issue.action && (
            <button className="mt-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
              {issue.action}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AuditResultDisplay({ result }: { result: QuoteAuditResult }) {
  const [expanded, setExpanded] = useState(true)

  const scoreColor =
    result.score >= 80 ? 'text-green-600' : result.score >= 60 ? 'text-amber-600' : 'text-red-600'

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className={cn('w-5 h-5', scoreColor)} />
          <span className={cn('text-2xl font-bold', scoreColor)}>{result.score}</span>
          <span className="text-sm text-slate-500">/100</span>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-2">
          {result.issues?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-600 mb-1">Issues ({result.issues.length})</p>
              <div className="space-y-1.5">
                {result.issues.slice(0, 3).map((issue, i) => <AuditIssueCard key={i} issue={issue} type="issue" />)}
              </div>
            </div>
          )}
          {result.warnings?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-amber-600 mb-1">Warnings ({result.warnings.length})</p>
              <div className="space-y-1.5">
                {result.warnings.slice(0, 3).map((warning, i) => <AuditIssueCard key={i} issue={warning} type="warning" />)}
              </div>
            </div>
          )}
          {result.suggestions?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-blue-600 mb-1">Suggestions ({result.suggestions.length})</p>
              <div className="space-y-1.5">
                {result.suggestions.slice(0, 3).map((suggestion, i) => <AuditIssueCard key={i} issue={suggestion} type="suggestion" />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
